// Commands — the only way state changes from outside the sim (docs/ARCHITECTURE.md §6).
// Money is the only cooldown: a command is valid whenever the player can afford it and its doctrine finds a
// place for it (GDD §7). Deploys and strikes name only what is bought: the sim's doctrines choose the city, the
// target and the launch site (GDD §7.1).

import type { TaxSetting } from '@taxwar/content'
import type { BuildingTypeId, PlayerId, SimId, StrikeTypeId, UnitTypeId } from './ids'

export type Command =
  | { readonly kind: 'deploy'; readonly unitType: UnitTypeId }
  | { readonly kind: 'build'; readonly buildingType: BuildingTypeId; readonly city: SimId }
  | { readonly kind: 'strike'; readonly strikeType: StrikeTypeId }
  | { readonly kind: 'setTax'; readonly setting: TaxSetting }
  /**
   * Concede the war (GDD §10): the other side wins by surrender. An online room also sends it for a seat that stayed
   * away past its reconnect grace (ARCHITECTURE §15).
   */
  | { readonly kind: 'surrender' }

/** `seq` strictly increases per player; seq <= last applied is a duplicate and is ignored. */
export interface CommandEnvelope {
  readonly player: PlayerId
  readonly seq: number
  readonly command: Command
}

/** Player errors are values (a CommandRejected event), never exceptions. */
export const RejectReason = {
  unaffordable: 'unaffordable',
  notYourCity: 'not_your_city',
  cityFallen: 'city_fallen',
  deploySiteMissing: 'deploy_site_missing',
  notCoastal: 'not_coastal',
  noFreeSlot: 'no_free_slot',
  noTargetInSight: 'no_target_in_sight',
  bodyCapReached: 'body_cap_reached',
  strikesInFlightCap: 'strikes_in_flight_cap',
  matchNotRunning: 'match_not_running',
  rateLimited: 'rate_limited',
} as const
export type RejectReason = (typeof RejectReason)[keyof typeof RejectReason]
